import type { Lang } from "@/i18n";
import { SITE, alternates } from "./site";

type PageKey = "home" | "services" | "references" | "about" | "faq" | "tools" | "contact";

interface MetaArgs {
  lang: Lang;
  page: PageKey;
  title: string;
  description: string;
  image?: string;
  jsonLd?: Array<Record<string, unknown>>;
}

const localeMap: Record<Lang, string> = {
  fr: "fr_BE",
  en: "en",
  nl: "nl_BE",
};

export function buildHead({ lang, page, title, description, image, jsonLd = [] }: MetaArgs) {
  const alt = alternates(page);
  const url = alt[lang];
  // Only emit og:image when an explicit image is provided for this page.
  // The sitewide default is added once in src/routes/__root.tsx from site_settings,
  // so we don't duplicate it here.
  const meta = [
    { title },
    { name: "description", content: description },
    { name: "author", content: SITE.name },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
    { property: "og:locale", content: localeMap[lang] },
    { property: "og:site_name", content: SITE.name },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];
  if (page === "faq") {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }
  if (image) {
    meta.push({ property: "og:image", content: image });
    meta.push({ name: "twitter:image", content: image });
  }

  const links = [
    { rel: "canonical", href: url },
    { rel: "alternate", hreflang: "fr-BE", href: alt.fr },
    { rel: "alternate", hreflang: "en", href: alt.en },
    { rel: "alternate", hreflang: "nl-BE", href: alt.nl },
    { rel: "alternate", hreflang: "x-default", href: alt.fr },
  ];

  const baseLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: SITE.name,
    description,
    url,
    foundingDate: "2022",
    logo: `${SITE.url}/logo.png`,
    image: image ?? `${SITE.url}/logo.png`,
    telephone: SITE.phoneRaw,
    email: SITE.email,
    founder: {
      "@type": "Person",
      name: SITE.founder,
      sameAs: SITE.linkedin,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address.street,
      postalCode: SITE.address.postal,
      addressLocality: SITE.address.city,
      addressCountry: SITE.address.country,
    },
    areaServed: [
      { "@type": "Country", name: "Belgium" },
      { "@type": "Country", name: "Luxembourg" },
    ],
    serviceType: [
      "Hotel operations management",
      "Hospitality consulting",
      "Hotel pre-opening",
      "Hotel opening",
      "Hotel management",
      "Hospitality IT and automation",
      "Hotel operating permits",
      "Short-stay operations",
      "Hybrid hospitality consulting",
    ],
    priceRange: "€€€",
    sameAs: [SITE.linkedin, "https://www.linkedin.com/company/2am-hospitality/"],
  };

  const scripts = [
    {
      type: "application/ld+json",
      children: JSON.stringify(baseLd),
    },
    ...jsonLd.map((data) => ({
      type: "application/ld+json",
      children: JSON.stringify(data),
    })),
  ];

  return { meta, links, scripts };
}
